import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import CompanyOptionLabel from "./CompanyOptionLabel";
import { Autocomplete, Box, Chip, CircularProgress, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  cleanAutocompleteText,
  dedupeAutocompleteStrings,
  normalizeAutocompleteText,
} from "../utils/autocompleteOptions";

const cleanValue = (value) => cleanAutocompleteText(value);
const normalizeValue = (value) => normalizeAutocompleteText(value);
const defaultFilterOptions = createFilterOptions();
const DEFAULT_FREE_SOLO_COMMIT_KEYS = ["Enter", "Tab", "|"];
const splitCommittedValues = (
  value,
  { splitFreeSoloOnPipe = true, splitFreeSoloOnComma = false } = {}
) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return [];
  if (!splitFreeSoloOnPipe && !splitFreeSoloOnComma) return [cleaned];

  const delimiters = [
    splitFreeSoloOnPipe ? "\\|" : "",
    splitFreeSoloOnComma ? "," : "",
  ].filter(Boolean);

  if (!delimiters.length) return [cleaned];

  return String(value ?? "")
    .split(new RegExp(`\\s*(?:${delimiters.join("|")})\\s*`, "g"))
    .map((item) => cleanValue(item))
    .filter(Boolean);
};

export const popupFilterInputSx = (accentColor = "#9C4FFF") => ({
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "12px",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: accentColor, borderWidth: 2 },
  },
  "& .MuiAutocomplete-inputRoot": {
    alignItems: "center",
    pr: "12px !important",
    py: "3px !important",
  },
  "& .MuiOutlinedInput-input": {
    boxSizing: "border-box",
    padding: "10px 12px",
    fontSize: 13,
    fontFamily: "Be Vietnam Pro",
    color: "#475467",
  },
  "& .MuiAutocomplete-input": {
    minWidth: "0 !important",
    fontSize: 13,
    fontFamily: "Be Vietnam Pro",
    color: "#475467",
    padding: "10px 4px 10px 12px !important",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#98A2B3",
    opacity: 1,
  },
});

export const popupFilterChipSx = {
  height: 28,
  borderRadius: "999px",
  backgroundColor: "#EAF2FF",
  color: "#3451F4",
  fontWeight: 500,
  fontSize: 13,
  fontFamily: "Be Vietnam Pro",
  "& .MuiChip-label": {
    px: 1.4,
  },
  "& .MuiChip-deleteIcon": {
    color: "#3451F4",
    fontSize: 18,
    mr: 0.5,
    "&:hover": {
      color: "#1D4ED8",
    },
  },
};

export default function PopupMultiSelectField({
  placeholder,
  companyJobCounts = false,
  options = [],
  value = [],
  onChange,
  onInputValueChange,
  loading = false,
  freeSolo = true,
  inputSx,
  chipSx,
  chipsBelow = true,
  renderAvatar,
  textFieldProps,
  autocompleteProps,
  splitFreeSoloOnPipe = true,
  splitFreeSoloOnComma = false,
  freeSoloCommitKeys = DEFAULT_FREE_SOLO_COMMIT_KEYS,
}) {
  const [inputValue, setInputValue] = useState("");
  const [optimisticValue, setOptimisticValue] = useState(null);
  // Autocomplete can fire its blur handler immediately after onChange.  Keep
  // the just-selected values in a ref so that blur never rebuilds the list
  // from the previous render and drops the final selected item.
  const selectedValuesRef = useRef([]);
  const pendingValueRef = useRef(null);
  const commitSelectionChange = useCallback(
    (nextValue = [], nextInputValue = "") => {
      const normalizedNextValue = dedupeAutocompleteStrings(nextValue);
      selectedValuesRef.current = normalizedNextValue;
      pendingValueRef.current = normalizedNextValue;
      const update = () => {
        setOptimisticValue(normalizedNextValue);
        setInputValue(nextInputValue);
        onInputValueChange?.(nextInputValue);
        onChange?.(normalizedNextValue);
      };
      if (freeSolo) flushSync(update);
      else update();
    },
    [freeSolo, onChange, onInputValueChange]
  );

  const normalizedOptions = useMemo(() => dedupeAutocompleteStrings(options), [options]);
  const normalizedValue = useMemo(() => dedupeAutocompleteStrings(value), [value]);
  const valuesMatch = useCallback(
    (left = [], right = []) =>
      left.length === right.length &&
      left.every((item) => right.some((candidate) => normalizeValue(item) === normalizeValue(candidate))),
    []
  );
  const isSubsetOf = useCallback(
    (subset = [], values = []) =>
      subset.every((item) => values.some((candidate) => normalizeValue(item) === normalizeValue(candidate))),
    []
  );
  useEffect(() => {
    const pendingValue = pendingValueRef.current;
    // A parent render from immediately before an option selection can arrive
    // after the selection event.  Do not let that stale, shorter value remove
    // the final chip while the parent catches up with our committed value.
    if (pendingValue) {
      if (valuesMatch(normalizedValue, pendingValue)) {
        pendingValueRef.current = null;
        setOptimisticValue(null);
      } else if (isSubsetOf(normalizedValue, pendingValue)) {
        return;
      } else {
        pendingValueRef.current = null;
        setOptimisticValue(null);
      }
    }
    selectedValuesRef.current = normalizedValue;
  }, [isSubsetOf, normalizedValue, valuesMatch]);
  const displayedValue = optimisticValue || normalizedValue;
  const optionExists = useCallback(
    (candidate) =>
      [...normalizedOptions, ...normalizedValue].some(
        (item) => normalizeValue(item) === normalizeValue(candidate)
      ),
    [normalizedOptions, normalizedValue]
  );
  const filterOptions = useCallback(
    (nextOptions, params) => {
      const filtered = dedupeAutocompleteStrings(defaultFilterOptions(nextOptions, params));
      const nextInput = cleanValue(params?.inputValue);
      if (!freeSolo || !nextInput) return filtered;

      return optionExists(nextInput)
        ? filtered
        : dedupeAutocompleteStrings([...filtered, nextInput]);
    },
    [freeSolo, optionExists]
  );

  useEffect(() => {
    if (!normalizedValue.length && !pendingValueRef.current) {
      selectedValuesRef.current = [];
      setInputValue("");
      onInputValueChange?.("");
    }
  }, [normalizedValue.length, onInputValueChange]);

  const commitFreeSoloValue = useCallback(
    (rawValue) => {
      if (!freeSolo) return false;

      const nextItems = splitCommittedValues(rawValue, {
        splitFreeSoloOnPipe,
        splitFreeSoloOnComma,
      });
      if (!nextItems.length) return false;

      const nextNormalizedValues = [...selectedValuesRef.current];
      let hasChanges = false;

      nextItems.forEach((nextItem) => {
        const exists = nextNormalizedValues.some(
          (item) => normalizeValue(item) === normalizeValue(nextItem)
        );
        if (exists) return;
        nextNormalizedValues.push(nextItem);
        hasChanges = true;
      });

      if (!hasChanges) {
        flushSync(() => {
          setInputValue("");
          onInputValueChange?.("");
        });
        return false;
      }
      commitSelectionChange(nextNormalizedValues);
      return true;
    },
    [
      commitSelectionChange,
      freeSolo,
      onInputValueChange,
      splitFreeSoloOnComma,
      splitFreeSoloOnPipe,
    ]
  );

  const handleDelete = useCallback(
    (target) => {
      commitSelectionChange(
        selectedValuesRef.current.filter((item) => normalizeValue(item) !== normalizeValue(target))
      );
    },
    [commitSelectionChange]
  );
  const shouldKeepDelimiterInInput = useCallback(
    (key) =>
      (key === "," && !splitFreeSoloOnComma) || (key === "|" && !splitFreeSoloOnPipe),
    [splitFreeSoloOnComma, splitFreeSoloOnPipe]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {!!displayedValue.length && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, order: chipsBelow ? 1 : 0, mt: chipsBelow ? 1.4 : 0, mb: chipsBelow ? 0 : 1.4 }}>
          {displayedValue.map((item, index) => (
            <Chip data-popup-selection-chip
              key={`${item}-${index}`}
              label={item}
              avatar={renderAvatar?.(item)}
              onDelete={() => handleDelete(item)}
              sx={[popupFilterChipSx, chipSx]}
            />
          ))}
        </Box>
      )}
      <Autocomplete
        multiple
        freeSolo={freeSolo}
        disableCloseOnSelect
        openOnFocus
        filterSelectedOptions={false}
        selectOnFocus
        handleHomeEndKeys
        includeInputInList={freeSolo}
        clearOnBlur={false}
        options={normalizedOptions}
        value={displayedValue}
        filterOptions={filterOptions}
        getOptionLabel={(option) => cleanValue(option)}
        inputValue={inputValue}
        onInputChange={(_, nextInput, reason) => {
          if (reason === "reset" || reason === "clear") {
            setInputValue("");
            onInputValueChange?.("");
            return;
          }
          setInputValue(nextInput);
          onInputValueChange?.(nextInput);
        }}
        onChange={(_, nextValue, reason) => {
          // MUI may emit a blur change after the selection event with the
          // previous controlled value.  The input blur below commits any
          // free-solo text from the ref, so this event must not replace the
          // already committed selection list.
          if (reason === "blur") return;
          commitSelectionChange(nextValue);
        }}
        renderOption={(props, option, { selected }) => {
          const normalizedOption = cleanValue(option);
          const shouldRenderCreateLabel =
            freeSolo &&
            normalizedOption &&
            normalizeValue(normalizedOption) === normalizeValue(inputValue) &&
            !optionExists(normalizedOption);

          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                "&[aria-selected='true']": {
                  backgroundColor: "#F4EBFF !important",
                  color: "#7C3AED",
                  fontWeight: 700,
                },
              }}
            >
              <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {renderAvatar?.(normalizedOption)}
                {shouldRenderCreateLabel ? `Add "${normalizedOption}"` : companyJobCounts ? <CompanyOptionLabel company={normalizedOption} /> : normalizedOption}
              </Box>
              {selected ? (
                <Box component="span" aria-label="Selected" sx={{ color: "#7C3AED", fontWeight: 800 }}>
                  ✓
                </Box>
              ) : null}
            </Box>
          );
        }}
        renderTags={() => null}
        isOptionEqualToValue={(option, selected) => normalizeValue(option) === normalizeValue(selected)}
        noOptionsText={
          autocompleteProps?.noOptionsText ??
          (freeSolo ? "Type to add a new entry" : "No options")
        }
        renderInput={(params) => {
          const extraInputProps = textFieldProps?.InputProps || {};
          const handleBlur = textFieldProps?.onBlur;
          const handleKeyDown = textFieldProps?.onKeyDown;

          return (
            <TextField
              {...params}
              {...textFieldProps}
              size={textFieldProps?.size || "small"}
              placeholder={placeholder}
              sx={[popupFilterInputSx(), inputSx, textFieldProps?.sx]}
              onBlur={(event) => {
                // Commit before MUI processes the native blur, which prevents
                // autoSelect/freeSolo blur handling from racing a new chip.
                commitFreeSoloValue(inputValue);
                params.inputProps?.onBlur?.(event);
                handleBlur?.(event);
              }}
              onKeyDown={(event) => {
                handleKeyDown?.(event);
                if (!freeSolo) return;
                if (event.defaultPrevented) return;

                if (shouldKeepDelimiterInInput(event.key)) {
                  event.stopPropagation();
                  return;
                }

                if (
                  freeSoloCommitKeys.includes(event.key) &&
                  inputValue.trim()
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  commitFreeSoloValue(inputValue);
                }
              }}
              InputProps={{
                ...params.InputProps,
                ...extraInputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {extraInputProps.endAdornment}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
        {...autocompleteProps}
        slotProps={{
          ...autocompleteProps?.slotProps,
          popper: {
            ...autocompleteProps?.slotProps?.popper,
            placement: "bottom-start",
            modifiers: [
              ...(autocompleteProps?.slotProps?.popper?.modifiers || []),
              { name: "flip", enabled: false },
            ],
          },
        }}
      />
    </Box>
  );
}
